import { useMemo } from 'react';

import type { CartItem, ProductPackage } from '@src/domains/cart';
import type { DetailedSaleItem } from '@src/domains/orders';
import { getFormattedTariffs, getPackageDates } from '@src/features/orders';
import { useAppSelector } from '@src/core/hooks';
import useCurrencyFormat from '@src/hooks/useCurrencyFormat';
import { appInfoSelector } from '@src/stores/app';

import {
  getSummaryPackagePriceSummary,
  type SummaryLineItem,
} from '@src/features/transactables/summary/core/summaryLineItem';

const formatSummaryPackageTariff = (quantity: number, name: string) => `${quantity}x ${name}`;

type UseSummaryPackageCardParams = {
  productPackage: ProductPackage;
  items: SummaryLineItem[];
};

export const useSummaryPackageCard = (params: UseSummaryPackageCardParams) => {
  const { productPackage, items } = params;
  const { formatCents } = useCurrencyFormat();
  const appInfo = useAppSelector(appInfoSelector);

  const packageDates = useMemo(() => getPackageDates(items as CartItem[] | DetailedSaleItem[]), [items]);
  const formattedTariffs = useMemo(
    () =>
      getFormattedTariffs(items as CartItem[] | DetailedSaleItem[], {
        format: formatSummaryPackageTariff,
      }),
    [items],
  );

  const { totalCents, discountCents, previousCents } = useMemo(() => getSummaryPackagePriceSummary(items), [items]);
  const packageSeller = items[0]?.product.app;
  const showSeller = Boolean(packageSeller && items[0]?.product.appId !== appInfo?.id);

  return {
    formatCents,
    productPackage,
    packageDates,
    formattedTariffs,
    totalCents,
    discountCents,
    previousCents,
    packageSeller,
    showSeller,
    items,
  };
};

export default useSummaryPackageCard;
